<template>
  <section class="booking-widget component">
    <!-- Default slot is where we put the activator
         checkAvailability: Function,
         runRouteValidation: Function,
         currentHotel: String,
         deactivate: Function,
         outerActivator: HTMLElement,
         defaultComponent: String,
         backArrow: true
    -->
    <slot v-bind="defaultScope" />
    <transition name="slide-right">
      <div
        v-if="isActive"
        class="booking-widget-wrapper"
        :class="[sidebarWidth]"
        role="dialog"
      >
        <!-- Step component -->
        <transition
          name="fade"
          mode="out-in"
        >
          <component
            :is="currentPage"
            v-bind="bindings"
            @change-page="currentPage = $event"
            @change-width="sidebarWidth = $event"
            @change-login-component="loginComponent = $event"
            @back="currentPage = BOOKING_WIDGET_PAGES.CONFIRMATION"
            @auth-done="currentPage = BOOKING_WIDGET_PAGES.CONFIRMATION"
          />
        </transition>
      </div>
    </transition>
    <transition name="fade">
      <div
        v-if="isActive"
        class="backlight"
        @click="deactivate"
      />
    </transition>
  </section>
</template>

<script>
  import $ from 'jquery'
  import { mapActions, mapGetters, mapState } from 'vuex'
  import { getDate } from '@utils/date-helpers'
  import { track } from '@plugins/tracking'
  import HotelList from './components/hotel-list/hotel-list'
  import Confirmation from './components/confirmation/confirmation'
  import GuestsAndRooms from './components/guests-and-rooms/guests-and-rooms'
  import CallToBook from './components/call-to-book/call-to-book'
  import AuthSidebar from '../../02.account/auth-activator/auth-sidebar.vue'
  import SpecialCode from './components/special-code/special-code'
  import HotelAlerts from './components/hotel-alerts/hotel-alerts'
  import Calendar from './components/calendar-scroll/calendar-scroll'
  /**
   * Handles first phase of Booking Engine.
   * - **:s**-property is inside slot scope
   * - **:p**-property is provided for all child components
   */

  const BOOKING_WIDGET_PAGES = Object.freeze({
    HOTEL_LIST: 'hotel-list',
    CALENDAR: 'calendar',
    GUESTS_AND_ROOMS: 'guests-and-rooms',
    CONFIRMATION: 'confirmation'
  })

  export default {
    name: 'BookingWidget',
    components: {
      HotelList,
      Calendar,
      Confirmation,
      GuestsAndRooms,
      CallToBook,
      AuthSidebar,
      SpecialCode,
      HotelAlerts
    },
    provide() {
      return {
        localQuery: this.localQuery,
        updateLocalQuery: this.updateLocalQuery,
        defaultCheckedForRegistration: this.defaultCheckedForRegistration,
        userTitles: this.userTitles
      }
    },
    props: {
      /**
       * URl to redirect the user to after criteria selection
       */
      bookingUrl: {
        type: String
      },
      /**
       * Set of translated labels to be registered in the [i18n store module](/docs/api/store/i18n)
       */
      lang: {
        type: Object,
        default: () => ({})
      },
      /**
       * Array of hotel data to be registered in the [hotelData store module](/docs/api/store/hoteldata)
       */
      hotelOptions: {
        type: Array,
        default: () => []
      },
      /**
       * Array of countries data to be registered in the [global store module](/docs/api/store/global)
       */
      countries: {
        type: Array,
        default: () => []
      },
      /**
       * Array of currencies available on site to be registered in the [currency store module](/docs/api/store/global)
       */
      currencies: {
        type: Array,
        default: () => []
      },
      /**
       * Labels used by the calendar to be registered in the [i18n store module](/docs/api/store/i18n)
       */
      daysAndMonths: {
        type: Object,
        default: () => ({})
      },
      /**
       * Content for the login components (Registration, Login, and JoinFans)
       */
      loginRegistration: {
        type: Object,
        default: () => ({})
      },
      /**
       * Sets the selected hotel option by default, it wil skip the first step **:s**.
       */
      currentHotel: {
        type: String,
      },
      /**
       * Room code(s) to be added in the search criteria
       */
      roomCodes: {
        type: [String, Array]
      },
      /**
       * Rate code to be added in the search criteria
       */
      rateCode: {
        type: String
      },
      /**
       * Promotion code to be added in the search criteria
       */
      promotionCode: {
        type: String
      },
      /**
       * Month and Year to be the first Month of the Calendar
       */
      jumpMonth: {
        type: String
      },
      /**
       * Bed type code to be added in the search criteria
       */
      firstBedTypeCode: {
        type: String
      },
      /**
       * When set to true won't configure initial data on created hook
       */
      dontRunConfig: Boolean,
      /**
       * Set the component as the main one
       */
      isMain: Boolean,
      /**
       * When set to true it will auto-check checkboxes on Sidebar Registration
       */
      defaultCheckedForRegistration: {
        type: Boolean,
        default: false
      },
      /**
       * User titles
       */
      userTitles: {
        type: Array,
        default: () => []
      },
      /**
       * Flag to know if user is accessing from California (US)
       */
      isCalifornia: {
        type: Boolean,
        default: false
      },
      /**
       * Flag to know if user is accessing from Chile (CL)
       */
      isChile: {
        type: Boolean,
        default: false
      },
      /**
       * Array of properties that have taxes
       */
      propertiesWithTax: {
        type: Array,
        default: () => []
      },
      /**
       * Force the Travel Advisor flow even without IATA in the Query
       */
      isFanClubBooking: {
        type: Boolean,
        default: false
      }
    },
    data: () => ({
      /**
       * @vuese
       * Control the activation of the component
       */
      isActive: false,
      /**
       * @vuese
       * Tracks when the component activation is coming from an external link click. (Main Widget only)
       */
      isExternalOpen: false,
      /**
       * @vuese
       * Controls the sidebar width (used on login/registration page transition)
       */
      sidebarWidth: 'default',
      /**
       * @vuese
       * Tracks witch component of the login should open by default (used on login/registration page)
       */
      loginComponent: 'login',
      /**
       * @vuese
       * We store a copy of the QueryOptions from the store on activation and we only update the store after the last widget step **:p**.
       */
      localQuery: {},
      /**
       * @vuese
       * Used to create return functionality on the runRouteValidation
       */
      overwritePage: null,
      /**
       * @vuese
       * The current page active in the widget
       */
      currentPage: BOOKING_WIDGET_PAGES.HOTEL_LIST,
      /**
       * @vuese
       * Used for accessibility, stores the HTML tag that activated the widget **:s**.
       */
      outerActivator: null
    }),
    computed: {
      ...mapState('booking', { bookingQuery: 'query', componentIsConfigured: 'configured' }),
      ...mapState('global', { currentLanguage: 'lang' }),
      ...mapState('modifyReservation', ['isBookingEditMode']),
      ...mapGetters('booking', ['dayFormat', 'getBookingTrackingV2']),
      bedTypeCode() {
        return this.firstBedTypeCode
      },
      bindings() {
        return {
          checkAvailability: this.checkAvailability,
          runRouteValidation: this.runRouteValidation,
          currentHotel: this.currentHotel,
          deactivate: this.deactivate,
          outerActivator: this.outerActivator,
          defaultComponent: this.loginComponent,
          backArrow: true
        }
      },
      defaultScope() {
        return {
          activate: this.activate,
          localQuery: this.localQuery
        }
      },
      isModifyBookingRoute() {
        // Check for the modify booking route or IsBookingEditMode, if active hide the booking widget
        // We don't want to mix the two flows
        // Need explore all use cases for this.
        return !!((this.$route?.path.includes('/booking/') && this.isBookingEditMode))
      }
    },
    watch: {
      /**
       * @vuese
       * On activation we initiate the localQuery
       */
      isActive(val) {
        document.body.classList.add('no-scroll')
        // Clone query only if the activation is not coming from an external activator
        if (val && !this.isExternalOpen) {
          this.cloneQuery()
        }
        // When config cycle is not run on load we need to validate the route on activation
        if (val && this.dontRunConfig) {
          this.runRouteValidation()
        }
        if (!val) {
          // Reset the component data
          if (this.isExternalOpen) {
            this.cloneQuery(true)
            this.currentPage = BOOKING_WIDGET_PAGES.HOTEL_LIST
            this.isExternalOpen = false
          }
          // Track exit event
          this.$track({ event: 'bookingWidgetExit' })
        }
      },
    },
    async created() {
      if (!this.dontRunConfig && !this.componentIsConfigured) {
        await this.configureComponent()
      }
      await this.initComponent()
    },
    async mounted() {
      if (this.isMain) {
        await this.configureMainBehavior()
        this.$root.$emit('booking-widget-mounted')
        this.toggleBookingEditMode(this.isModifyBookingRoute) // Ensure modify booking mode is reset
      }
    },
    methods: {
      ...mapActions('i18n', ['registerScopedLang', 'registerLang']),
      ...mapActions('hotelData', ['registerHotels']),
      ...mapActions('booking', ['updateQuery', 'componentConfigured']),
      ...mapActions('rooms', ['getAvailableRooms']),
      ...mapActions('cart', ['resetCart', 'resetRoomUpgrades']),
      ...mapActions('auth', ['updateAuthComponents']),
      ...mapActions('global', ['registerCountriesData', 'updateIsCalifornia', 'updateIsChile', 'updatePropertiesWithTax', 'updateIsFanClubBooking']),
      ...mapActions('currency', ['registerCurrencies']),
      ...mapActions('modifyReservation', ['toggleBookingEditMode', 'restartModifyBookingEngine']),
      /**
       * @vuese
       * Set the specific functionality for the MainWidget on mounted hook
       */
      async configureMainBehavior() {
        // Implementation for main booking-widget to activate is a openBooking query parameter
        // is in the url.
        const queryParams = new URLSearchParams(window.location.search)
        const openBooking = queryParams.get('openBooking')
        if (openBooking === 'true') {
          this.isActive = true
        }
        // Event to run when a anchor tag with the target="booking"
        // is clicked.
        const onClick = (e) => {
          e.preventDefault()
          this.isExternalOpen = true
          const url = e.currentTarget.href
          // Get the search params in the href property of the anchor
          const localParams = new URLSearchParams(url)
          this.cloneQuery(true)
          for (let [key, value] of localParams.entries()) {
            if (key.includes('?')) {
              key = key.replace(`${key.split('?')[0]}?`, '')
            }
            if (key in this.bookingQuery) {
              // Update each property of the localQuery based on the search parameters
              this.updateLocalQuery({ prop: key, value })

              if (key === 'rateCode' && this.localQuery.promotionCode === 'fanclub') {
                this.updateLocalQuery({ prop: 'promotionCode', value: '' })
              }
            }
          }
          this.runRouteValidation()
          this.isActive = true
        }
        // Bind the click events
        $(document).on('click', 'a[target^="book"]', onClick)
      },
      /**
       * @vuese
       * Register all basic data to the store on created hook
       */
      async configureComponent() {
        await this.componentConfigured()
        const start = new Date().valueOf()
        this.$logger.debug('configuring booking widget')
        await this.registerHotels(this.hotelOptions)
        await this.registerLang({ scope: 'booking', lang: this.lang })
        await this.registerScopedLang({ scope: 'calendar', lang: this.daysAndMonths })
        await this.registerScopedLang({ scope: 'authentication', lang: this.loginRegistration })
        await this.registerCountriesData(this.countries.map((c) => ({
          Name: c.label, IsoCode: c.value, CallingCode: c.callingCode, States: c.states
        })).filter((c) => !!c.Name))
        await this.registerCurrencies(this.currencies)
        await this.updateAuthComponents(this.loginRegistration)
        await this.updateIsCalifornia(this.isCalifornia)
        await this.updateIsChile(this.isChile)
        await this.updatePropertiesWithTax(this.propertiesWithTax)
        await this.updateIsFanClubBooking(this.isFanClubBooking)
        const end = new Date().valueOf()
        this.$logger.debug('configuration ended in:', `${end - start}ms`)
      },
      /**
       * @vuese
       * Init basic functionality, presets data to the localQuery on created hook
       */
      async initComponent() {
        if (this.currentHotel) {
          await this.configureHotel()
        }
        if (this.roomCodes) {
          const roomCodes = Array.isArray(this.roomCodes) ? this.roomCodes : [this.roomCodes]
          this.updateLocalQuery({ prop: 'roomCodes', value: roomCodes.map((c) => c.trim()) })
          if (this.bedTypeCode) {
            this.updateLocalQuery({ prop: 'roomCodes', value: [this.bedTypeCode] })
          }
        }
        if (this.rateCode) {
          this.updateLocalQuery({ prop: 'rateCode', value: this.rateCode })
        }
        if (this.promotionCode) {
          this.updateLocalQuery({ prop: 'promotionCode', value: this.promotionCode })
        }
        if (this.jumpMonth) {
          this.updateLocalQuery({ prop: 'jumpMonth', value: this.jumpMonth })
        }
        if (typeof this.firstBedTypeCode === 'string') {
          this.updateLocalQuery({ prop: 'bedType', value: this.firstBedTypeCode })
        }
        if (!this.dontRunConfig) {
          await this.runRouteValidation()
        }
      },
      /**
       * @vuese
       * Create the local query from the [Booking Store Module](/docs/api/store/booking)
       * @param {boolean} withDefault - force all properties to be cloned
       */
      cloneQuery(withDefault = false) {
        const queryParams = new URLSearchParams(window.location.search)
        Object.keys(this.bookingQuery).forEach((key) => {
          if (this.dontRunConfig || (!this.localQuery[key] || withDefault)) {
            const urlParam = queryParams.get(key) || this.bookingQuery[key]
            const value = this.dontRunConfig ? this.bookingQuery[key] : urlParam
            this.$set(this.localQuery, key, value)
          }
        })
      },
      /**
       * @vuese
       * Set the hotel code as criteria and skips the hotel-list page
       * @param {string} hotel
       */
      async configureHotel(hotel) {
        this.updateLocalQuery({ prop: 'hotelCode', value: hotel || this.currentHotel })
        this.currentPage = BOOKING_WIDGET_PAGES.CALENDAR
      },
      /**
       * @vuese
       * Validates witch page to move next based on criteria selection
       */
      runRouteValidation() {
        if (this.overwritePage) {
          this.currentPage = this.overwritePage
          this.overwritePage = null
        } else if (!this.localQuery.hotelCode) {
          this.currentPage = BOOKING_WIDGET_PAGES.HOTEL_LIST
        } else if (!this.localQuery.stayDateStart || !this.localQuery.stayDateEnd) {
          this.currentPage = BOOKING_WIDGET_PAGES.CALENDAR
        } else if (!this.localQuery.adultGuestCount) {
          this.currentPage = BOOKING_WIDGET_PAGES.GUESTS_AND_ROOMS
        } else {
          this.currentPage = BOOKING_WIDGET_PAGES.CONFIRMATION
        }
      },
      /**
       * @vuese
       * Activate the component
       * @arg Two arguments the first the page it should open first and the second the HTMLElement that triggered the activation
       * @param {string} page - page name
       * @param {HTMLElement} activator - Element that triggered the activation
       */
      async activate(page, activator) {
        if (typeof page === 'string') {
          this.overwritePage = page
        }
        if (activator) {
          this.outerActivator = activator
        }
        if (!this.isActive && !activator) {
          track({
            event: 'cartOpen'
          })
        }
        await this.registerHotels(this.hotelOptions)
        this.isActive = true
        // Resets all the session storage for Modify Booking Engine
        if (!this.isModifyBookingRoute) {
          this.restartModifyBookingEngine()
        }
      },
      /**
       * @vuese
       * Deactivate the component
       */
      async deactivate() {
        this.isActive = false
        this.$track(this.getBookingTrackingV2(this.localQuery, { event: 'cartRemove' }))
        this.$nextTick(() => {
          this.outerActivator = false
          document.body.classList.remove('no-scroll')
        })
      },
      /**
       * @vuese
       * Run when the last step is confirmed, if there is a bookingUrl
       * it will transform the localQuery to an URL query parameter string
       * and redirect the user for this url, if not it will update the
       * [Booking Store](/docs/api/store/booking) query
       * and restart the booking flow.
       */
      async checkAvailability() {
        try {
          await this.resetCart()
          if (this.bookingUrl) {
            const isFractal = window.location.host.includes('localhost:3000')
            const lang = isFractal ? '' : `/${this.currentLanguage}`

            const queryParams = Object.keys(this.localQuery)
              .map((key) => {
                if (Array.isArray(this.localQuery[key])) {
                  return `${key}=${JSON.stringify(this.localQuery[key])}`
                }
                return `${key}=${this.localQuery[key]}`
              })
              .join('&')
            await new Promise((resolve) => setTimeout(resolve, 1000))
            window.location.href = `${lang}${this.bookingUrl}/#/booking/?${queryParams}`
          } else {
            await this.updateQuery({ query: this.localQuery })
            this.$store.commit('booking/SELECT_ROOM', 0)
            this.resetRoomUpgrades()
            await this.getAvailableRooms()
            await this.deactivate()
          }
        } catch (error) {
          console.error('Error in checkAvailability:', error)
        }
      },
      /**
       * @vuese
       * Mimics the updateQuery action from the [Booking Store Module](/docs/api/store/booking)
       * but updates only the local query, one property at a time.
       * @param {{prop: string, value: any}} param0
       */
      updateLocalQuery({ prop, value }) {
        let propValue = value
        if (['roomCodes', 'aditionalGuests'].includes(prop) && typeof value === 'string') {
          propValue = JSON.parse(value)
        }
        if (typeof propValue === 'string') {
          propValue = propValue.trim()
        } else if (Array.isArray(propValue) && typeof propValue[0] === 'string') {
          propValue = propValue.map((v) => v.trim())
        }
        this.$set(this.localQuery, prop, propValue)
        if (prop === 'stayDateStart' && (!this.localQuery.stayDateEnd || getDate(propValue).isSameOrAfter(getDate(this.localQuery.stayDateEnd), 'day'))) {
          this.$set(this.localQuery, 'stayDateEnd', getDate(propValue).add(1, 'day').format(this.dayFormat))
        } else if (prop === 'stayDateEnd' && getDate(propValue).isSameOrBefore(getDate(this.localQuery.stayDateStart), 'day')) {
          this.$set(this.localQuery, 'stayDateStart', getDate(propValue).subtract(1, 'day').format(this.dayFormat))
        }
      }
    }
  }
</script>
